'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import type { ChangeEvent, FormEvent, JSX } from 'react';
import { useId, useState } from 'react';
import type { TradingCompetitionSummary } from '@/components/social/competition/types';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Icon } from '@/components/ui/icon';
import { InformationCircleIconSolid, Wallet01Icon } from '@/components/ui/icon/glyphs';
import { ModalFooter } from '@/components/ui/modal-footer';
import { SilkSheet } from '@/components/ui/silk-sheet';
import { TextareaField } from '@/components/ui/textarea-field';
import { isSolanaAddress } from '@/lib/competitions/payout-wallet';
import { useFormattedCurrency } from '@/lib/currency';
export interface PayoutWalletSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  competition: TradingCompetitionSummary;
  /** The wallet on file, if any — the same address applies to every competition. */
  currentAddress: string | null;
  saving: boolean;
  onSave: (address: string) => void;
}

// One external Solana address per user. Validation here is the base58 shape;
// the server also checks the key is on-curve and not a program-derived address.
export function PayoutWalletSheet(t0) {
  const $ = _c(105);
  const {
    open,
    onOpenChange,
    competition,
    currentAddress,
    saving,
    onSave
  } = t0;
  const t = useTranslations("competitions.wallet");
  const {
    formatD6
  } = useFormattedCurrency();
  const formId = useId();
  const [address, setAddress] = useState(currentAddress ?? "");
  let invalid;
  let t1;
  let trimmed;
  if ($[0] !== address || $[1] !== currentAddress || $[2] !== saving) {
    trimmed = address.trim();
    invalid = trimmed.length > 0 && !isSolanaAddress(trimmed);
    t1 = !saving && isSolanaAddress(trimmed) && trimmed !== currentAddress;
    $[0] = address;
    $[1] = currentAddress;
    $[2] = saving;
    $[3] = invalid;
    $[4] = t1;
    $[5] = trimmed;
  } else {
    invalid = $[3];
    t1 = $[4];
    trimmed = $[5];
  }
  const canSave = t1;
  let t2;
  if ($[6] !== canSave || $[7] !== onSave || $[8] !== trimmed) {
    t2 = function handleSubmit(event) {
      event.preventDefault();
      if (!canSave) {
        return;
      }
      onSave(trimmed);
    };
    $[6] = canSave;
    $[7] = onSave;
    $[8] = trimmed;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const handleSubmit = t2;
  let t3;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Icon icon={Wallet01Icon} className="size-icon-lg text-foreground-brand" />;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== currentAddress || $[12] !== t) {
    t4 = currentAddress === null ? t("addTitle") : t("changeTitle");
    $[11] = currentAddress;
    $[12] = t;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== t4) {
    t5 = <span className="flex items-center gap-2">{t3}{t4}</span>;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== t) {
    t6 = t("description");
    $[16] = t;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== onOpenChange) {
    t7 = () => onOpenChange(false);
    $[18] = onOpenChange;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t) {
    t8 = t("cancel");
    $[20] = t;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== saving || $[23] !== t7 || $[24] !== t8) {
    t9 = <AnimatedButton variant="secondary" onClick={t7} disabled={saving}>{t8}</AnimatedButton>;
    $[22] = saving;
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  const t10 = !canSave;
  let t11;
  if ($[26] !== saving || $[27] !== t) {
    t11 = saving ? t("saving") : t("save");
    $[26] = saving;
    $[27] = t;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== formId || $[30] !== saving || $[31] !== t10 || $[32] !== t11) {
    t12 = <AnimatedButton variant="primary" type="submit" form={formId} disabled={t10} loading={saving} data-testid="payout-wallet-save">{t11}</AnimatedButton>;
    $[29] = formId;
    $[30] = saving;
    $[31] = t10;
    $[32] = t11;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== t12 || $[35] !== t9) {
    t13 = <ModalFooter>{t9}{t12}</ModalFooter>;
    $[34] = t12;
    $[35] = t9;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  let t14;
  if ($[37] !== t) {
    t14 = t("label");
    $[37] = t;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== t) {
    t15 = t("placeholder");
    $[39] = t;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  let t16;
  if ($[41] !== invalid || $[42] !== t) {
    t16 = invalid ? t("invalid") : undefined;
    $[41] = invalid;
    $[42] = t;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  let t17;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = event_0 => setAddress(event_0.target.value);
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  let t18;
  if ($[45] !== address || $[46] !== saving || $[47] !== t14 || $[48] !== t15 || $[49] !== t16) {
    t18 = <TextareaField label={t14} name="payoutWallet" value={address} placeholder={t15} rows={2} spellCheck={false} autoComplete="off" autoCapitalize="off" disabled={saving} fullWidth={true} error={t16} onChange={t17} className="min-h-20 resize-none break-all px-3 py-3 font-mono leading-relaxed" data-testid="payout-wallet-input" />;
    $[45] = address;
    $[46] = saving;
    $[47] = t14;
    $[48] = t15;
    $[49] = t16;
    $[50] = t18;
  } else {
    t18 = $[50];
  }
  let t19;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <Icon icon={InformationCircleIconSolid} aria-hidden="true" className="mt-0.5 size-icon-md shrink-0 text-feedback-info-accent" />;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  let t20;
  if ($[52] !== t) {
    t20 = t("devnetNote");
    $[52] = t;
    $[53] = t20;
  } else {
    t20 = $[53];
  }
  let t21;
  if ($[54] !== t20) {
    t21 = <div role="note" className="flex items-start gap-2 text-foreground-secondary">{t19}<p className="text-caption leading-relaxed">{t20}</p></div>;
    $[54] = t20;
    $[55] = t21;
  } else {
    t21 = $[55];
  }
  let t22;
  if ($[56] !== t) {
    t22 = t("appliesTo");
    $[56] = t;
    $[57] = t22;
  } else {
    t22 = $[57];
  }
  let t23;
  if ($[58] !== t22) {
    t23 = <dt className="text-caption text-foreground-tertiary">{t22}</dt>;
    $[58] = t22;
    $[59] = t23;
  } else {
    t23 = $[59];
  }
  let t24;
  if ($[60] !== t) {
    t24 = t("everyCompetition");
    $[60] = t;
    $[61] = t24;
  } else {
    t24 = $[61];
  }
  let t25;
  if ($[62] !== t24) {
    t25 = <dd className="text-body font-medium text-foreground-primary">{t24}</dd>;
    $[62] = t24;
    $[63] = t25;
  } else {
    t25 = $[63];
  }
  let t26;
  if ($[64] !== t23 || $[65] !== t25) {
    t26 = <div className="flex min-w-0 flex-col gap-1">{t23}{t25}</div>;
    $[64] = t23;
    $[65] = t25;
    $[66] = t26;
  } else {
    t26 = $[66];
  }
  let t27;
  if ($[67] !== t) {
    t27 = t("thisCompetition");
    $[67] = t;
    $[68] = t27;
  } else {
    t27 = $[68];
  }
  let t28;
  if ($[69] !== t27) {
    t28 = <dt className="text-caption text-foreground-tertiary">{t27}</dt>;
    $[69] = t27;
    $[70] = t28;
  } else {
    t28 = $[70];
  }
  let t29;
  if ($[71] !== competition.title) {
    t29 = <span className="block break-words font-medium">{competition.title}</span>;
    $[71] = competition.title;
    $[72] = t29;
  } else {
    t29 = $[72];
  }
  let t30;
  if ($[73] !== competition.prizePoolD6 || $[74] !== formatD6) {
    t30 = formatD6(competition.prizePoolD6, "compact");
    $[73] = competition.prizePoolD6;
    $[74] = formatD6;
    $[75] = t30;
  } else {
    t30 = $[75];
  }
  let t31;
  if ($[76] !== t30) {
    t31 = <span className="text-caption text-foreground-secondary">{t30}</span>;
    $[76] = t30;
    $[77] = t31;
  } else {
    t31 = $[77];
  }
  let t32;
  if ($[78] !== t29 || $[79] !== t31) {
    t32 = <dd className="text-body text-foreground-primary">{t29}{t31}</dd>;
    $[78] = t29;
    $[79] = t31;
    $[80] = t32;
  } else {
    t32 = $[80];
  }
  let t33;
  if ($[81] !== t28 || $[82] !== t32) {
    t33 = <div className="flex min-w-0 flex-col gap-1">{t28}{t32}</div>;
    $[81] = t28;
    $[82] = t32;
    $[83] = t33;
  } else {
    t33 = $[83];
  }
  let t34;
  if ($[84] !== t26 || $[85] !== t33) {
    t34 = <dl className="grid grid-cols-1 gap-4 border-t border-border-secondary pt-4 sm:grid-cols-2">{t26}{t33}</dl>;
    $[84] = t26;
    $[85] = t33;
    $[86] = t34;
  } else {
    t34 = $[86];
  }
  let t35;
  if ($[87] !== t) {
    t35 = t("changeNote");
    $[87] = t;
    $[88] = t35;
  } else {
    t35 = $[88];
  }
  let t36;
  if ($[89] !== t35) {
    t36 = <p className="text-caption text-foreground-tertiary">{t35}</p>;
    $[89] = t35;
    $[90] = t36;
  } else {
    t36 = $[90];
  }
  let t37;
  if ($[91] !== formId || $[92] !== handleSubmit || $[93] !== t18 || $[94] !== t21 || $[95] !== t34 || $[96] !== t36) {
    t37 = <form id={formId} onSubmit={handleSubmit} className="flex min-w-0 flex-col gap-5">{t18}{t21}{t34}{t36}</form>;
    $[91] = formId;
    $[92] = handleSubmit;
    $[93] = t18;
    $[94] = t21;
    $[95] = t34;
    $[96] = t36;
    $[97] = t37;
  } else {
    t37 = $[97];
  }
  let t38;
  if ($[98] !== onOpenChange || $[99] !== open || $[100] !== t13 || $[101] !== t37 || $[102] !== t5 || $[103] !== t6) {
    t38 = <SilkSheet variant="detached" size="md" keyboard={true} open={open} onOpenChange={onOpenChange} title={t5} description={t6} footer={t13}>{t37}</SilkSheet>;
    $[98] = onOpenChange;
    $[99] = open;
    $[100] = t13;
    $[101] = t37;
    $[102] = t5;
    $[103] = t6;
    $[104] = t38;
  } else {
    t38 = $[104];
  }
  return t38;
}